import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { managementService } from '../service/management.service';
import { IOfflineActivationResult } from '../types.type';

/**
 * Offline (air-gapped) activation, done from the signed-in website.
 *
 * Studios on MPA-aligned content-security programmes block outbound traffic
 * from artist workstations by default, so those machines can never reach a
 * licence server. The user carries a request blob out of the secure network,
 * pastes it here, and carries the signed .lic file back — which is why store
 * login is the anchor for this whole feature.
 *
 * Collapsed by default: most customers are online and should not have to read
 * past a workflow that does not apply to them.
 */
export const OfflineActivation = ({ queryKey }: { queryKey: string[] }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const download = (result: IOfflineActivationResult) => {
    if (!result.token) return;
    // The .lic is plain text, so it is assembled in the browser rather than
    // fetched again — nothing here needs a second round trip.
    const blob = new Blob([result.token], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const activate = useMutation({
    mutationFn: () => managementService.activateOffline(request.trim()),
    onSuccess: (result) => {
      download(result);
      setRequest('');
      toast.success(t('management.eye.license.offline.success'));
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: any) => {
      // The server's machine-readable code is the whole message here: a
      // replayed request and an unreadable one need different actions from
      // the user, so they must not collapse into one generic failure.
      const code =
        error?.response?.data?.error ?? error?.data?.error ?? error?.error;
      const key =
        code === 'NONCE_ALREADY_USED'
          ? 'management.eye.license.offline.errorUsed'
          : code === 'BAD_REQUEST_BLOB'
            ? 'management.eye.license.offline.errorBlob'
            : code === 'SEAT_LIMIT'
              ? 'management.eye.license.offline.errorSeats'
              : 'management.eye.license.offline.error';
      toast.error(t(key));
    }
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file
      .text()
      .then((text) => setRequest(text.trim()))
      .catch(() => toast.error(t('management.eye.license.offline.errorBlob')));
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-[14px] inline-flex items-center gap-[6px] px-[10px] h-[28px] rounded-[8px] bg-surface-hover border border-blue-500 text-blue font-mono text-[10.5px] tracking-wide uppercase hover:bg-surface-hover-section transition-colors cursor-pointer"
      >
        {t('management.eye.license.offline.cta')}
      </button>
    );
  }

  return (
    <div className="mt-[14px] px-[14px] py-[13px] rounded-[10px] bg-surface-section border border-border-dark/[0.11]">
      <div className="flex items-start justify-between gap-[10px] mb-[8px]">
        <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light">
          {t('management.eye.license.offline.title')}
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-content-muted-alt hover:text-content-dark text-[18px] leading-none"
        >
          ×
        </button>
      </div>

      <p className="font-body text-[13px] text-content-secondary leading-[1.6] mb-[12px]">
        {t('management.eye.license.offline.help')}
      </p>

      <textarea
        value={request}
        onChange={(e) => setRequest(e.target.value)}
        rows={4}
        spellCheck={false}
        placeholder={t('management.eye.license.offline.placeholder')}
        className="w-full rounded-[8px] border border-border-dark/[0.15] bg-surface-base px-[10px] py-[8px] font-mono text-[11px] text-content-secondary resize-y focus:outline-none focus:border-blue"
      />

      <div className="flex items-center gap-[10px] flex-wrap mt-[10px]">
        <button
          type="button"
          disabled={!request.trim() || activate.isPending}
          onClick={() => activate.mutate()}
          className={`inline-flex items-center justify-center py-[9px] px-[18px] rounded-[9px] text-[13px] font-semibold bg-blue border-[1.5px] border-blue text-surface-base transition-all duration-200 ${
            !request.trim() || activate.isPending
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-surface-popular hover:border-surface-popular cursor-pointer'
          }`}
        >
          {activate.isPending
            ? t('management.eye.license.offline.activating')
            : t('management.eye.license.offline.activate')}
        </button>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="inline-flex items-center px-[12px] h-[34px] rounded-[9px] border border-border-dark/[0.15] text-[12px] text-content-secondary hover:bg-surface-hover transition-colors cursor-pointer"
        >
          {t('management.eye.license.offline.upload')}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".sbreq,.txt,.json,text/plain"
          onChange={onFile}
          className="hidden"
        />
      </div>

      <p className="mt-[10px] font-body italic text-[11.5px] text-content-muted-alt leading-[1.5]">
        {t('management.eye.license.offline.note')}
      </p>
    </div>
  );
};
